// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Hatch — the menus, help, about and results markup. Results follow principle #9: the solo
// screen shows the knowable "perfect" (a clean, zero-mistake solve), and the multiplayer
// screen shows EVERY player's score AND how cleanly each solved it, not just this tab's.

import { escapeHtml, fmtScore, fmtTime } from './dom';
import { MODE_IDS, modeOf, type Mode, type ModeId } from './modes';
import { progress, type GameState } from './game';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(o: { modeId: ModeId; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  const chips = MODE_IDS.map(
    (id) =>
      `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
  return `
    <section class="screen menu">
      <h1 class="logo">Hatch</h1>
      <p class="tagline">Fill the squares the numbers describe — and a hidden picture hatches.</p>
      <div class="modes" role="group" aria-label="Mode">${chips}</div>
      <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
      <div class="menu-actions">
        <button class="btn primary" id="playSolo">Play</button>
        <button class="btn" id="playDaily">Daily board · ${escapeHtml(o.dateStr)}</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <p class="best-line">${o.best > 0 ? `Best in ${escapeHtml(mode.name)}: <strong>${fmtScore(o.best)}</strong>` : 'No score yet — go read some numbers.'}</p>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>How to play</h2>
    <p>The numbers count the <strong>filled squares</strong>. Each row and column lists the lengths of
    its runs of filled squares, in order — <strong>3&nbsp;1</strong> means three-in-a-row, a gap, then
    a lone one. Work out which squares must be filled, and reveal the hidden picture.</p>
    <ul class="help-list">
      <li><strong>Tap to fill</strong> a square, or <strong>drag</strong> to paint a whole run.</li>
      <li>Not sure? Switch to <strong>Mark&nbsp;✕</strong> and note a square as empty — marks are free.</li>
      <li>But <strong>filling a square that's really empty is a mistake</strong> — so read the numbers
      before you commit.</li>
      <li><strong>Zen</strong> — one 10×10 picture, no clock, no strikes. Just you and the numbers.</li>
      <li><strong>Blitz</strong> — a stream of 5×5 pictures in 90 seconds; a mistake costs you time.</li>
      <li><strong>Strikes</strong> — an 8×8 picture, three strikes and you're out. Be certain.</li>
    </ul>
    <p class="help-controls"><strong>On a keyboard:</strong> arrow keys move the cursor,
    <kbd>Space</kbd> fills, <kbd>X</kbd> marks, <kbd>F</kbd>/<kbd>X</kbd> switch tools. Pause any time.</p>
    <button class="btn primary sheet-close">Got it</button>`;
}

export function aboutHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>About Hatch</h2>
    <p>A picross / nonogram: a grid of numbered clues hiding a picture you reveal by pure logic. Every
    board is <em>generated to be solvable by deduction alone</em> — never a guess — with a single unique
    solution. All boards, art and sound are generated in the browser; nothing is downloaded.</p>
    <p>Play solo, chase the UTC <strong>daily board</strong>, or share a room and race friends on the
    identical picture.</p>
    <p class="about-privacy"><strong>Multiplayer &amp; privacy.</strong> Rooms are peer-to-peer over
    WebRTC — there is no game server. A public signalling relay only helps two browsers find each other;
    no game data is stored on any server, and no cookies or trackers are used beyond an anonymous,
    cookie-less page-view count.</p>
    <p>${ATTRIB}</p>`;
}

// ── results ─────────────────────────────────────────────────────────────────────────────
function statLine(s: GameState): string {
  const bits: string[] = [];
  if (s.mode.stream) {
    bits.push(`Pictures <strong>${s.banked}</strong>`);
    bits.push(`Mistakes <strong>${s.mistakes}</strong>`);
  } else if (s.won) {
    bits.push(`Time <strong>${fmtTime(s.elapsedMs)}</strong>`);
    bits.push(s.mistakes === 0 ? '<strong>Clean solve!</strong>' : `Mistakes <strong>${s.mistakes}</strong>`);
  } else {
    bits.push(`Filled <strong>${Math.round(progress(s) * 100)}%</strong>`);
    bits.push(`Mistakes <strong>${s.mistakes}${s.mode.maxStrikes ? `/${s.mode.maxStrikes}` : ''}</strong>`);
  }
  return bits.join(' · ');
}

function headlineFor(s: GameState): string {
  if (s.mode.stream) return "Time's up";
  if (s.won) return s.mistakes === 0 ? 'Hatched — flawless!' : 'Picture hatched!';
  if (s.mode.maxStrikes) return 'Three strikes — the picture got away';
  return "Time's up";
}

export function soloResultsHtml(o: { state: GameState; best: number; isNewBest: boolean; daily: boolean }): string {
  const s = o.state;
  return `
    <section class="screen results">
      <h2 class="results-head">${escapeHtml(headlineFor(s))}</h2>
      <div class="score-big">${fmtScore(s.score)}</div>
      ${o.isNewBest ? '<div class="new-best">New best!</div>' : `<div class="prev-best">Best in ${escapeHtml(s.mode.name)}: ${fmtScore(o.best)}</div>`}
      <p class="stat-line">${statLine(s)}</p>
      ${o.daily ? '<p class="daily-note">Everyone gets the same daily board.</p>' : ''}
      <button class="linkbtn results-feedback" id="rfeedback">Report a problem</button>
    </section>`;
}

export function multiResultsHtml(ranked: PeerResult[], selfSeat: number | null, mode: Mode, tally: number[]): string {
  const live = ranked.filter((x) => !x.left).length;
  const rows = ranked
    .map((r, i) => {
      const isSelf = r.seat === selfSeat;
      const rank = r.left ? '—' : `#${i + 1}`;
      const winner = i === 0 && !r.left && live > 1 && r.score > 0;
      const wins = tally[r.seat] ?? 0;
      let detail: string;
      if (r.left) detail = 'left the room';
      else if (!r.finished) detail = 'still playing…';
      else if (mode.stream) detail = `${r.mistakes} mistake${r.mistakes === 1 ? '' : 's'}`;
      else if (r.solved) detail = r.mistakes === 0 ? 'clean solve' : `revealed · ${r.mistakes} mistake${r.mistakes === 1 ? '' : 's'}`;
      else detail = `didn't finish · ${r.mistakes} mistake${r.mistakes === 1 ? '' : 's'}`;
      return `
        <div class="mres-row${isSelf ? ' self' : ''}${r.left ? ' left' : ''}">
          <span class="mres-rank">${rank}</span>
          <span class="mres-name">${winner ? '★ ' : ''}${escapeHtml(r.name)}${isSelf ? ' (you)' : ''}${wins > 0 ? ` <em>· ${wins}★</em>` : ''}</span>
          <span class="mres-detail">${detail}</span>
          <span class="mres-score">${r.left ? '' : fmtScore(r.score)}</span>
        </div>`;
    })
    .join('');
  return `
    <h2 class="results-head">${escapeHtml(mode.name)} · results</h2>
    <div class="mres-list">${rows}</div>`;
}
